<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import type { VForm } from 'vuetify/components'
import { useHardwareProfileStore } from '@/stores/hardwareProfile'
import type {
  HardwareProfile,
  HardwareProfileFormPayload,
  VirtualMachineSizeOption
} from '@/stores/type/calculation.type'

const DEFAULT_DEPLOYMENT_REGION = 'westeurope'

const props = defineProps<{
  modelValue: boolean
  item: HardwareProfile
  projectId?: number | null
  loading?: boolean
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  submit: [value: HardwareProfileFormPayload]
}>()

const formRef = ref<VForm>()
const isValid = ref(false)
const name = ref('')
const description = ref('')
const spotInstance = ref(false)
const vmMemoryRatio = ref<number | null>(null)
const vmSize = ref('')
const vmImage = ref('UBUNTU_2404_LTS_SERVER')
const vmOsDiskSizeGb = ref<number | null>(32)
const vmOsDiskStorageAccountType = ref('StandardSSD_LRS')
const deploymentRegion = ref('westeurope')
const cpuCores = ref<number | null>(4)
const memoryGb = ref<number | null>(4)

const hardwareProfileStore = useHardwareProfileStore()
const { hardwareOptions, hardwareOptionsLoading } = storeToRefs(hardwareProfileStore)

const dialog = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value)
})

const isEdit = computed(() => !!props.item?.id)

const formatRatioValue = (value: number) => {
  return Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })
}

const memoryRatioForVmSize = (option: { cpu_cores: number; memory_gb: number }) => {
  return option.memory_gb / option.cpu_cores
}

const isVmSizeAvailableInRegion = (option: VirtualMachineSizeOption) => {
  return !option.regions?.length || option.regions.includes(deploymentRegion.value)
}

const availableVmSizes = computed(() =>
  (hardwareOptions.value?.vm_sizes ?? []).filter(isVmSizeAvailableInRegion)
)

const selectedVmSize = computed(() =>
  availableVmSizes.value.find((option) => option.size === vmSize.value)
)

const vmMemoryRatioItems = computed(() => {
  const groups = new Map<number, Set<string>>()
  const vmSizes = availableVmSizes.value

  vmSizes.forEach((option) => {
    const ratio = memoryRatioForVmSize(option)
    const series = groups.get(ratio) ?? new Set<string>()
    series.add(option.series)
    groups.set(ratio, series)
  })

  return Array.from(groups.entries())
    .sort(([left], [right]) => left - right)
    .map(([ratio, series]) => ({
      title: `1 CPU : ${formatRatioValue(ratio)} GB RAM (${Array.from(series).sort().join(', ')})`,
      value: ratio
    }))
})

const vmSizeOptionsForSelectedRatio = computed(() => {
  return availableVmSizes.value.filter((option) => {
    return vmMemoryRatio.value === null || memoryRatioForVmSize(option) === vmMemoryRatio.value
  })
})

const vmSizeItems = computed(() =>
  vmSizeOptionsForSelectedRatio.value.map((option) => ({
    title: `${option.size} (${option.series}, ${option.cpu_cores} CPU, ${option.memory_gb} GB RAM)`,
    value: option.size
  }))
)

const vmImageItems = computed(() =>
  (hardwareOptions.value?.vm_images ?? []).map((option) => ({
    title: option.label,
    value: option.value
  }))
)

const vmOsDiskStorageAccountTypeItems = computed(() =>
  (hardwareOptions.value?.vm_os_disk_storage_account_types ?? []).map((option) => ({
    title: option.label,
    value: option.value
  }))
)

const vmOsDiskSizeItems = computed(() =>
  (hardwareOptions.value?.vm_os_disk_sizes ?? []).map((option) => {
    const skuName = option.sku_names?.[vmOsDiskStorageAccountType.value]
    return {
      title: skuName ? `${option.size_gb} GB (${skuName})` : `${option.size_gb} GB`,
      value: option.size_gb
    }
  })
)

const deploymentRegionItems = computed(() => [
  ...(hardwareOptions.value?.deployment_regions ?? [])
    .map((option) => ({
      title: option.label,
      value: option.name
    }))
])

const requiredText = 'Required.'
const nameRules = [(value: string) => !!value?.trim() || requiredText]
const vmSizeRules = [(value: string) => !!value || requiredText]
const vmMemoryRatioRules = [
  (value: number | null) => value !== null || requiredText
]
const vmImageRules = [(value: string) => !!value || requiredText]
const vmOsDiskSizeRules = [
  (value: number | null) => value !== null || requiredText
]
const vmOsDiskStorageAccountTypeRules = [(value: string) => !!value || requiredText]
const cpuRules = computed(() => [
  (value: number | null) => value !== null || requiredText,
  (value: number | null) => Number(value) > 0 || 'CPU cores must be greater than 0.'
])
const memoryRules = computed(() => [
  (value: number | null) => value !== null || requiredText,
  (value: number | null) => Number(value) > 0 || 'RAM must be greater than 0 GB.'
])

const fetchOptionsIfNeeded = () => {
  if (!hardwareOptions.value && !hardwareOptionsLoading.value) {
    void hardwareProfileStore.fetchHardwareOptions()
  }
}

const applySelectedVmSize = () => {
  if (!selectedVmSize.value) return
  cpuCores.value = selectedVmSize.value.cpu_cores
  memoryGb.value = selectedVmSize.value.memory_gb
}

const ensureVmMemoryRatio = () => {
  if (selectedVmSize.value) {
    const selectedRatio = memoryRatioForVmSize(selectedVmSize.value)
    if (vmMemoryRatio.value === null) {
      vmMemoryRatio.value = selectedRatio
      return
    }
  }

  const selectedRatioAvailable =
    vmMemoryRatio.value !== null &&
    vmMemoryRatioItems.value.some((option) => option.value === vmMemoryRatio.value)

  if (!selectedRatioAvailable && vmMemoryRatioItems.value.length) {
    vmMemoryRatio.value = vmMemoryRatioItems.value[0].value
  }
}

const ensureVmSize = () => {
  ensureVmMemoryRatio()
  const selectedSizeMatchesRatio =
    selectedVmSize.value &&
    vmMemoryRatio.value !== null &&
    memoryRatioForVmSize(selectedVmSize.value) === vmMemoryRatio.value

  if (!selectedSizeMatchesRatio && vmSizeItems.value.length) {
    vmSize.value = vmSizeItems.value[0].value
  }
  if (!vmSizeItems.value.length) {
    vmSize.value = ''
  }
  applySelectedVmSize()
}

const ensureVmImage = () => {
  if (!vmImage.value && vmImageItems.value.length) {
    vmImage.value = vmImageItems.value[0].value
  }
}

const ensureVmOsDisk = () => {
  if (!vmOsDiskStorageAccountType.value && vmOsDiskStorageAccountTypeItems.value.length) {
    vmOsDiskStorageAccountType.value = vmOsDiskStorageAccountTypeItems.value[0].value
  }
  if (
    vmOsDiskSizeGb.value === null ||
    !vmOsDiskSizeItems.value.some((option) => option.value === vmOsDiskSizeGb.value)
  ) {
    vmOsDiskSizeGb.value = vmOsDiskSizeItems.value[0]?.value ?? 32
  }
}

const resetForm = () => {
  name.value = props.item?.name ?? ''
  description.value = props.item?.description ?? ''
  spotInstance.value = Boolean(props.item?.spot_instance)
  vmSize.value = props.item?.vm_size ?? ''
  vmImage.value = props.item?.vm_image ?? 'UBUNTU_2404_LTS_SERVER'
  vmOsDiskSizeGb.value = Number(props.item?.vm_os_disk_size_gb ?? 32)
  vmOsDiskStorageAccountType.value =
    props.item?.vm_os_disk_storage_account_type ?? 'StandardSSD_LRS'
  deploymentRegion.value = props.item?.deployment_region || DEFAULT_DEPLOYMENT_REGION
  vmMemoryRatio.value = selectedVmSize.value ? memoryRatioForVmSize(selectedVmSize.value) : null
  cpuCores.value = Number(props.item?.cpu_cores ?? 8)
  memoryGb.value = Number(props.item?.memory_gb ?? 64)
  ensureVmSize()
  ensureVmImage()
  ensureVmOsDisk()
  formRef.value?.resetValidation()
}

const close = () => {
  dialog.value = false
  resetForm()
}

const submit = async () => {
  const validationResult = await formRef.value?.validate()
  if (!validationResult?.valid) return
  if (cpuCores.value === null || memoryGb.value === null) return

  emit('submit', {
    id: props.item?.id || undefined,
    name: name.value.trim(),
    description: description.value,
    project: props.projectId ?? null,
    hardware_type: 'VIRTUAL_MACHINE',
    spot_instance: spotInstance.value,
    vm_size: vmSize.value,
    vm_image: vmImage.value,
    vm_os_disk_size_gb: vmOsDiskSizeGb.value ?? 32,
    vm_os_disk_storage_account_type: vmOsDiskStorageAccountType.value,
    deployment_region: deploymentRegion.value,
    cpu_cores: cpuCores.value,
    memory_gb: memoryGb.value
  })
}

watch(
  () => props.item,
  () => resetForm(),
  { immediate: true }
)

watch(dialog, (value) => {
  if (value) {
    fetchOptionsIfNeeded()
    resetForm()
  }
})

watch(vmSize, () => {
  if (selectedVmSize.value) {
    vmMemoryRatio.value = memoryRatioForVmSize(selectedVmSize.value)
  }
  applySelectedVmSize()
})

watch(vmMemoryRatio, () => {
  ensureVmSize()
})

watch(spotInstance, () => {
  void formRef.value?.validate()
})

watch(deploymentRegion, () => {
  ensureVmSize()
  void formRef.value?.validate()
})

watch(hardwareOptions, () => {
  ensureVmSize()
  ensureVmImage()
  ensureVmOsDisk()
})
</script>

<template>
  <v-dialog v-model="dialog" max-width="560" @click:outside="close">
    <v-card class="form-dialog pa-3">
      <v-card-title>{{
        isEdit ? 'Edit hardware profile' : 'Create hardware profile'
      }}</v-card-title>

      <v-card-text class="pl-4">
        <v-form ref="formRef" v-model="isValid" @submit.prevent="submit">
          <v-text-field
            v-model="name"
            color="primary"
            label="Name"
            :rules="nameRules"
            variant="filled"
            required
          />

          <v-textarea
            v-model="description"
            auto-grow
            color="primary"
            label="Description"
            rows="3"
            variant="filled"
          />

          <v-select
            v-model="deploymentRegion"
            color="primary"
            item-title="title"
            item-value="value"
            label="Deployment region"
            :items="deploymentRegionItems"
            :loading="hardwareOptionsLoading"
            :menu-props="{ zIndex: 2500 }"
            variant="filled"
          />

          <v-checkbox
            v-model="spotInstance"
            color="primary"
            density="compact"
            hide-details
            label="Spot instance"
          />

          <v-select
            v-model="vmMemoryRatio"
            color="primary"
            item-title="title"
            item-value="value"
            label="CPU to RAM ratio"
            :items="vmMemoryRatioItems"
            :loading="hardwareOptionsLoading"
            :menu-props="{ zIndex: 2500 }"
            :rules="vmMemoryRatioRules"
            variant="filled"
            required
          />

          <v-autocomplete
            v-model="vmSize"
            auto-select-first
            color="primary"
            item-title="title"
            item-value="value"
            label="VM size"
            :items="vmSizeItems"
            :loading="hardwareOptionsLoading"
            :menu-props="{ zIndex: 2500 }"
            :rules="vmSizeRules"
            variant="filled"
            required
          />

          <v-select
            v-model="vmImage"
            color="primary"
            item-title="title"
            item-value="value"
            label="VM image"
            :items="vmImageItems"
            :loading="hardwareOptionsLoading"
            :menu-props="{ zIndex: 2500 }"
            :rules="vmImageRules"
            variant="filled"
            required
          />

          <v-row dense>
            <v-col cols="12" sm="6">
              <v-select
                v-model="vmOsDiskStorageAccountType"
                color="primary"
                item-title="title"
                item-value="value"
                label="OS disk type"
                :items="vmOsDiskStorageAccountTypeItems"
                :loading="hardwareOptionsLoading"
                :menu-props="{ zIndex: 2500 }"
                :rules="vmOsDiskStorageAccountTypeRules"
                variant="filled"
                required
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-select
                v-model="vmOsDiskSizeGb"
                color="primary"
                item-title="title"
                item-value="value"
                label="OS disk size"
                :items="vmOsDiskSizeItems"
                :loading="hardwareOptionsLoading"
                :menu-props="{ zIndex: 2500 }"
                :rules="vmOsDiskSizeRules"
                variant="filled"
                required
              />
            </v-col>
          </v-row>

          <v-row dense>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model.number="cpuCores"
                color="primary"
                label="CPU cores"
                min="0.01"
                step="0.01"
                type="number"
                :rules="cpuRules"
                variant="filled"
                disabled
                required
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model.number="memoryGb"
                color="primary"
                label="RAM"
                min="0.01"
                step="0.01"
                suffix="GB"
                type="number"
                :rules="memoryRules"
                variant="filled"
                disabled
                required
              />
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn class="text-none" color="primary" :disabled="loading" @click="close"> Cancel </v-btn>
        <v-btn
          class="text-none"
          color="primary"
          :loading="loading"
          :disabled="!isValid"
          @click="submit"
        >
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
